import React, { createContext, Suspense, useContext, useState } from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import Loading from '../../components/loading/Loading'
import { useDesktopWindowContext } from '../../pages/desktop/components/desktopWindow/DesktopWindowRenderer'
import {
  scrollWidth,
  windowMinWidth
} from '../../pages/desktop/desktop.constants'
import { TabContext, TabPanel } from '@mui/lab'
import EnvironmentSelector from 'src/applications/rates/components/EnvironmentSelector'
import styles from './Rates.modules.scss'
import RatesContextProvider, { useRatesContext } from './Rates.context'
import RatesDataGrid from './pages/ratesDataGrid/RatesDataGrid'

const RatesConfig = React.lazy(
  async () =>
    await import('src/applications/rates/pages/ratesConfig/RatesConfig')
)
const Coupons = React.lazy(
  async () => await import('src/applications/rates/pages/coupons/Coupons')
)

const Offers = React.lazy(async () => await import('./pages/offers/Offers'))

export enum RatesViews {
  Loading = 'Loading',
  RatesPlanning = 'RatesPlanning',
  OffersCreate = 'OffersCreate',
  OffersView = 'OffersView',
  CouponsCreate = 'CouponsCreate',
  CouponsView = 'CouponsView',
  Configuration = 'Configuration',
  ConfigurationView = 'ConfigurationView',
  RatesList = 'RatesList',
  CreateRateMenu = 'CreateRateMenu',
  CreateFlexibleRate = 'CreateFlexibleRate',
  CreateNonRefundableRate = 'CreateNonRefundableRate',
  CreateCustomRate = 'CreateCustomRate'
}

export enum RATES_TABS {
  PLANNING = 'PLANNING',
  OFFERS = 'OFFERS',
  COUPONS = 'COUPONS',
  CONFIG = 'CONFIG'
}

interface Props {
  defaultTab?: RATES_TABS
}

interface RatesRouterContextType {
  view: RatesViews
  setView: React.Dispatch<RatesViews>
  hideNavbar: () => void
  showNavbar: () => void
  isNavbarShow: 'show' | 'hide' | undefined
}

const RatesRouterContext = createContext<RatesRouterContextType | null>(null)

const RatesRouterComponent: ReactFC<Props> = ({ defaultTab }) => {
  const { windowId } = useDesktopWindowContext()
  const { environmentId } = useRatesContext()

  const [view, setView] = useState<RatesViews>(RatesViews.RatesPlanning)
  const [tab, setTab] = useState(defaultTab ?? RATES_TABS.PLANNING)
  const [isNavbarShow, setIsNavbarShow] = useState<'show' | 'hide'>()

  const tabTitles: { [key in keyof typeof RATES_TABS]: string } = {
    CONFIG: 'Configuración',
    COUPONS: 'Cupones',
    OFFERS: 'Ofertas',
    PLANNING: 'Planning Tarifas'
  }

  const handleChangeTab = (event: React.SyntheticEvent, newTab: RATES_TABS) => {
    setTab(newTab)
  }

  const hideNavbar = () => {
    setIsNavbarShow('hide')
  }

  const showNavbar = () => {
    setIsNavbarShow('show')
  }

  return (
    <Box
      sx={{
        minWidth: `${windowMinWidth - scrollWidth}px`,
        height: 'max-content',
        bgcolor: 'background.default',
        padding: '13px',
        paddingLeft: {
          xs: '13px',
          xl: '2%'
        }
      }}
    >
      {view === RatesViews.Loading ? (
        <Loading />
      ) : (
        <>
          <Suspense fallback={<Loading />}>
            <RatesRouterContext.Provider
              value={{
                setView,
                view,
                hideNavbar,
                showNavbar,
                isNavbarShow
              }}
            >
              <TabContext value={tab as string}>
                <Box
                  sx={{ overflow: 'hidden' }}
                  className={
                    isNavbarShow === 'show'
                      ? styles.grow
                      : isNavbarShow === 'hide'
                      ? styles.ungrow
                      : ''
                  }
                >
                  <Tabs
                    sx={{
                      color: 'white',
                      backgroundColor: 'primary.main',
                      borderRadius: 2
                    }}
                    value={tab}
                    onChange={handleChangeTab}
                    textColor='inherit'
                    aria-label='secondary tabs example'
                    TabIndicatorProps={{
                      style: {
                        background: 'white'
                      }
                    }}
                  >
                    {(
                      Object.keys(RATES_TABS) as Array<keyof typeof RATES_TABS>
                    ).map((tab) => (
                      <Tab
                        value={RATES_TABS[tab]}
                        label={tabTitles[tab]}
                        key={`AppRateTab-${windowId}-${tab}`}
                      />
                    ))}
                    <Box
                      sx={{
                        marginLeft: 'auto',
                        marginTop: '3px',
                        marginRight: '1rem'
                      }}
                    >
                      <EnvironmentSelector />
                    </Box>
                  </Tabs>
                </Box>
                <Box
                  sx={{
                    '.MuiTabPanel-root': {
                      padding: 0
                    }
                  }}
                >
                  {!environmentId ? (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: 'background.default'
                      }}
                    >
                      <Typography
                        sx={{
                          marginTop: '1rem'
                        }}
                      >
                        Por favor seleccione un entorno
                      </Typography>
                      {/* EXPECTING TO BE CHANGED, TRADUCIR */}
                    </Box>
                  ) : (
                    <>
                      <TabPanel value={RATES_TABS.PLANNING}>
                        <RatesDataGrid />
                      </TabPanel>
                      <TabPanel value={RATES_TABS.CONFIG}>
                        <RatesConfig />
                      </TabPanel>
                      <TabPanel value={RATES_TABS.COUPONS}>
                        <Coupons />
                      </TabPanel>
                      <TabPanel value={RATES_TABS.OFFERS}>
                        <Offers />
                      </TabPanel>
                    </>
                  )}
                </Box>
              </TabContext>
            </RatesRouterContext.Provider>
          </Suspense>
        </>
      )}
    </Box>
  )
}

const RatesRouter = (props: Props) => {
  return (
    <RatesContextProvider>
      <RatesRouterComponent {...props} />
    </RatesContextProvider>
  )
}

export default RatesRouter

export const useRatesRouter = () =>
  useContext(RatesRouterContext) as RatesRouterContextType
